import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import { Container, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../routing/LangRouter";

//import redux store
import { saveHotelSelectedFilters, saveHotelSort, saveHotelData } from "../../store/actions";

//import constants
import { ItineraryContext } from "../../constants/layouts/itinerary-layout";
import { IS_MOBILE } from "../../constants/breakpoints";

//import utils
import { scrollToPosition } from "../../utils/utility";

//import customized hooks
import useHeaderInfo from "../../hooks/useHeaderInfo";
import useHotelList from "../../hooks/useHotelList";

//import general components
import Loading from "../../components/common/loading/loading";
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import BaseSort from "../../components/common/base-sort/base-sort";
import BasePagination from "../../components/common/base-pagination/base-pagination";
import FilterSortActions from "../../components/common/filter-sort-actions/filter-sort-actions";
import ArrowIcon from "../../components/common/arrow-icon/arrow-icon";
import ExpirationResultModal from "../../components/common/expiration-result-modal/expiration-result-modal";
import EmptyOrErrorState from "../../components/common/empty-or-error-state/empty-or-error-state";
import BaseTitle from "../../components/common/base-title/base-title";
import WithoutDestinationModal from "../../components/common/without-modal/without-destination-modal";
import WithoutDateModal from "../../components/common/without-modal/without-date-modal";
import WithoutDestinationDateModal from "../../components/common/without-modal/without-destination-date-modal";

//import view components
import HotelDetails from "../../components/view/hotel/hotel-details";
import HotelSearchForm from "../../components/view/hotel/hotel-search-form";
import HotelList from "../../components/view/hotel/hotel-list";
import HotelRooms from "../../components/view/hotel/hotel-rooms";
import HotelFilter from "../../components/view/hotel/hotel-filter";
import HotelSkeleton from "../../components/view/hotel/hotel-skeleton";
import HotelInformation from "../../components/view/hotel/hotel-information";

//import images and icons
import { ArrowLeft2, ArrowRight2, Edit2, InfoCircle, Warning2 } from "iconsax-react";
import RecommendedHotel from "../../components/view/hotel/recommended-hotel";

function HotelPage({ dispatch, hotelData, hotelSort, hotelSelectedFilters }) {
    //general variables
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const [loading, setLoading] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [requestFlag, setRequestFlag] = useState(false);
    const [isError, setIsError] = useState(false);

    //sessionStorage variables
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const { destination, checkIn, checkOut, nationality, type } = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const { isItinerary, isHotelInternational } = JSON.parse(sessionStorage.getItem("generalInformation"));

    //scroll to top
    useEffect(() => {
        scrollToPosition(0, 0);
    }, [loading]);

    //set hotel list
    const [hotelList, setHotelList] = useState([]);
    const [uid, setUid] = useState(hotelData?.uid);
    const [requestExpirationDate, setRequestExpirationDate] = useState("");
    useEffect(() => {
        if (!hotelData) {
            setHotelList([]);
            setUid(null);
            return;
        }

        setHotelList( hotelData?.results ?? hotelData?.data);
        setUid(hotelData?.uid);
        setRequestExpirationDate(hotelData?.exp_at);
    }, [hotelData, hotelSort]);

    //list of sorting options
    const optionListSort = [
        { value: "4", label: `${t("cheapest")}`, checked: false },
        { value: "3", label: `${t("expensive")}`, checked: false },
    ];

    // sort initial value
    useEffect(() => {
        dispatch(saveHotelSort("4"));
    }, []);

    //set value radio button for sort
    const handleSlectedRadio = value => {
        dispatch(saveHotelSort(value));
    };

    //skip to the itinerary page
    const skipLevel = () => {
        setRoutes([...routes, `/${locale}/itinerary/itinerary-page`]);
        sessionStorage.setItem("visitedRoutes", JSON.stringify([...routes, `/${locale}/itinerary/itinerary-page`]));
        navigate(`/${locale}/itinerary/itinerary-page`);
    };

    //this section handles the state and effects for displaying room details
    const [openInformation, setOpenInformation] = useState(false);
    const [openRooms, setOpenRooms] = useState(false);
    useEffect(() => {
        if (searchParams.get("hotelId") && searchParams.get("action") === "information") setOpenInformation(true);
        else setOpenInformation(false);

        if (searchParams.get("hotelId") && searchParams.get("action") === "rooms") setOpenRooms(true);
        else setOpenRooms(false);
    }, [searchParams]);

    //pagination
    const [page, setPage] = useState(hotelData?.current_page ?? 1);
    const handlePaginationChange = async (event, value) => {
        setPage(value);
        setUid(hotelData.uid);
        setRequestFlag(true);
    };

    //handle without destination,without date,and without destination-date modal
    const [openWithoutDate, setOpenWithoutDate] = useState(false);
    const [openWithoutDestination, setOpenWithoutDestination] = useState(false);
    const [openWithoutDestinationDate, setOpenWithoutDestinationDate] = useState(false);
    useEffect(() => {
        const { destination, checkIn, checkOut } = hotelInformation;

        if (destination !== null && checkIn === null && checkOut === null) setOpenWithoutDate(true);
        else setOpenWithoutDate(false);

        if (destination === null && !!checkIn && !!checkOut) setOpenWithoutDestination(true);
        else setOpenWithoutDestination(false);

        if (destination === null && checkIn === null && checkOut === null) setOpenWithoutDestinationDate(true);
        else setOpenWithoutDestinationDate(false);
    }, []);

    const [openSearchForm, setOpenSearchForm] = useState(false);
    const [openHotelDetails, setOpenHotelDetails] = useState(false);
    useEffect(() => {
        if (
            (isItinerary && !nationality && !openWithoutDate && !openWithoutDestination && !openWithoutDestinationDate) ||
            searchParams.get("action") === "edit"
        ) {
            setOpenSearchForm(true);
        } else {
            setOpenSearchForm(false);
        }

        if (IS_MOBILE) {
            if (searchParams.get("action") === "hotel-details") setOpenHotelDetails(true);
            else setOpenHotelDetails(false);
        }
    }, [searchParams, isItinerary, openWithoutDate, openWithoutDestination, openWithoutDestinationDate]);

    const { getHotelList } = useHotelList(
        isHotelInternational,
        hotelInformation,
        type,
        uid,
        page,
        hotelSort,
        hotelSelectedFilters,
        setRequestFlag,
        setLoading,
        setIsError,
        setOpenSearchForm,
    );

    useEffect(() => {
        // Only auto-load hotel list if not in itinerary mode or if form has been submitted
        if (destination !== null && !!checkIn && !!checkOut && !!nationality) {
            getHotelList();
        }
    }, []);

    useEffect(() => {
        if (requestFlag && !!nationality) getHotelList();
    }, [requestFlag]);

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveHotelSelectedFilters(null));
    };

    const resetHotelList = () => {
        dispatch(saveHotelData(null));
    };

    //mobile functions

    // Handle navigation for mobile back button
    const handleGoBack = () => {
        if (isItinerary) {
            const currentRouteIndex = routes.findIndex(route => route === location.pathname);
            const previousRoute = routes[currentRouteIndex - 1];
            navigate(previousRoute);
        } else {
            navigate(`/${locale}`);
        }
    };

    //header information
    const { headerTitle, headerDetails } = useHeaderInfo("hotel", hotelInformation);

    //handle show edit search form and flight details
    const handleEditSearch = action => {
        const params = { action };
        setSearchParams(params, { replace: true });
    };

    return (
        <Grid container item xs={12} rowGap={{ xs: 2, md: 5 }} justifyContent="center" mt={openSearchForm || IS_MOBILE ? 6 : 0} mb={5}>
            {loading && <Loading />}

            <MobileHeader
                title={headerTitle}
                details={headerDetails}
                edit={<Edit2 size="20" color="#006363" onClick={() => handleEditSearch("edit")} />}
                skip={
                    isItinerary && (
                        <ArrowIcon
                            icons={[ArrowLeft2, ArrowRight2]}
                            isExpanded={locale === "en"}
                            size="24"
                            color="var(--teal-color-18)"
                            onClick={skipLevel}
                        />
                    )
                }
                justifyContent="flex-start"
                isButton={false}
                handleClick={handleGoBack}
            />

            <HotelSearchForm openSearchForm={openSearchForm} setRequestFlag={setRequestFlag} setUid={setUid} />

            {openSearchForm && !nationality ? null : ( // Show only search form for itinerary when not submitted
                // Show hotel list and other content after form submission or for non-itinerary
                <>
                    {loading ? (
                        <HotelSkeleton />
                    ) : isError ? (
                        <EmptyOrErrorState
                            title={t("errorMessage")}
                            handleClick={getHotelList}
                            actionLabel={t("retry")}
                            icon={<Warning2 size="32" color="var(--red-color)" variant="Bold" />}
                        />
                    ) : (
                        <>
                            {!openHotelDetails && IS_MOBILE ? (
                                <Grid container item xs={11} mt={2}>
                                    <BaseTitle
                                        title={type === "hotel" ? `${t("hotelList")}` : `${t("accommodationList")}`}
                                        dividerColor="1px solid transparent"
                                        button={
                                            <FilterSortActions
                                                onFilterClick={() => setIsFilterOpen(true)}
                                                onSortClick={() => setIsSortOpen(true)}
                                                selectedFilters={hotelSelectedFilters}
                                            />
                                        }
                                    />
                                </Grid>
                            ) : null}

                            {!openHotelDetails && !openInformation && !openRooms && !isHotelInternational ? (
                                <RecommendedHotel hotelList={hotelList} />
                            ) : null}

                            <Container maxWidth="xl">
                                <Grid container item xs={12} justifyContent="center">
                                    {openHotelDetails ? (
                                        <HotelDetails />
                                    ) : openInformation ? (
                                        <HotelInformation />
                                    ) : openRooms ? (
                                        <HotelRooms />
                                    ) : !!hotelInformation?.destination ? (
                                        <Grid container item xs={12} justifyContent="space-between" alignItems="flex-start">
                                            <Grid
                                                container
                                                item
                                                xs={12}
                                                md={2}
                                                rowGap={2}
                                                justifyContent="center"
                                                sx={{ position: "sticky !important", top: "5rem", zIndex: 10 }}
                                            >
                                                <HotelFilter
                                                    open={isFilterOpen}
                                                    setOpen={setIsFilterOpen}
                                                    setPage={setPage}
                                                    setRequestFlag={setRequestFlag}
                                                    setUid={setUid}
                                                    type={type}
                                                />
                                            </Grid>

                                            <Grid container item xs={12} md={9.8} rowGap={2} alignItems="flex-start">
                                                <BaseSort
                                                    open={isSortOpen}
                                                    setOpen={setIsSortOpen}
                                                    optionList={optionListSort}
                                                    handleChange={handleSlectedRadio}
                                                    defaultValue={hotelSort}
                                                    setRequestFlag={setRequestFlag}
                                                    setPage={setPage}
                                                />

                                                <HotelList hotelList={hotelList} loading={loading} type={type} />
                                                <Grid container item xs={12} justifyContent="center">
                                                    {hotelList?.length > 1 && (
                                                        <BasePagination
                                                            count={hotelData?.total_pages}
                                                            page={hotelData?.current_page ?? 1}
                                                            handleChange={handlePaginationChange}
                                                        />
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <EmptyOrErrorState
                                            title={t("hotelListMessage")}
                                            icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />}
                                            actionLabel={isItinerary ? t("nextLevel") : ""}
                                            handleClick={isItinerary ? skipLevel : null}
                                        />
                                    )}
                                </Grid>
                            </Container>
                        </>
                    )}
                </>
            )}
            <WithoutDateModal open={openWithoutDate} setOpen={setOpenWithoutDate} setRequestFlag={setRequestFlag} />
            <WithoutDestinationModal open={openWithoutDestination} setOpen={setOpenWithoutDestination} setRequestFlag={setRequestFlag} />
            <WithoutDestinationDateModal open={openWithoutDestinationDate} setOpen={setOpenWithoutDestinationDate} setRequestFlag={setRequestFlag} />
            <ExpirationResultModal
                requestExpirationDate={requestExpirationDate}
                setRequestFlag={setRequestFlag}
                uid={uid}
                setUid={setUid}
                resetList={resetHotelList}
                resetFilters={resetFilters}
            />
        </Grid>
    );
}

export default connect(mapStateToProps)(HotelPage);

function mapStateToProps(state) {
    return {
        hotelData: state.hotelData,
        hotelSort: state.hotelSort,
        hotelSelectedFilters: state.hotelSelectedFilters,
    };
}
